import type { GetApiV1TeamTeamIdParams } from '@1webit/cvs-api-scheme/models';
import type {
  GetApiV1ClubMemberBeachDailyOverviewParams,
} from '@1webit/cvs-api-scheme/models/getApiV1ClubMemberBeachDailyOverviewParams';
import type { GetApiV1ClubMemberParams } from '@1webit/cvs-api-scheme/models/getApiV1ClubMemberParams';
import { queryOptions } from '@tanstack/react-query';

import { DEFAULT_TABLE_PAGE_SIZE } from '@/constants';
import type { BaseTableParams } from '@/utils';
import { dateToAPIFormatOptional, getResponse, paginationToApiFormat, queryParams } from '@/utils';

// list
export const getClubListQueryOptions = (page = 1, sort?: string, search?: string, status?: string, region?: number) => {
  return queryOptions({
    queryKey: ['getApiV1Club', status, region, page, sort, search],
    queryFn: () => {
      return getResponse('getApiV1Club', queryParams({
        limit: DEFAULT_TABLE_PAGE_SIZE,
        sort,
        offset: Math.max(0, Number(page) - 1) * DEFAULT_TABLE_PAGE_SIZE,
        fullText: search,
        ...(status || region
          ? {
              search: [
                status ? `canceled:${status === 'inactive' ? 'true' : 'false'}` : '',
                region ? `region:${region}` : '',
              ].filter(Boolean).join(';'),
            }
          : {}),
      }));
    },
  });
};
export const getApiV1ClubFavorite = (id: number) => {
  return queryOptions({
    queryKey: ['getApiV1ClubFavorite', id],
    queryFn: async () => {
      const res = await getResponse('getApiV1ClubFavorite');
      return {
        ...res,
        responseData: res.responseData.map(item => ({
          ...item,
          id: item.clubId,
        })),
      };
    },
  });
};

// detail
export const getApiV1ClubId = (id: number) => {
  return queryOptions({
    queryKey: ['getApiV1ClubId', id],
    queryFn: () => getResponse('getApiV1ClubId', id),
  });
};

// members
export const getClubMembersForNSA = (id: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, year: string, sort?: string, eventsCount?: string | number, qualifyForNSA?: string, fullText?: string, k1?: boolean, k2?: boolean) => {
  let search: string | undefined;
  if (eventsCount || qualifyForNSA) {
    if (eventsCount) {
      search = `eventsCount:${eventsCount}`;
    }
    if (qualifyForNSA) {
      if (search !== undefined) {
        search += `;qualifyForNSA:${qualifyForNSA === 'Ano' ? 1 : 0}`;
      } else {
        search = `qualifyForNSA:${qualifyForNSA === 'Ano' ? 1 : 0}`;
      }
    }
  }

  const params = queryParams({
    limit,
    offset: Math.max(0, Number(page) - 1) * limit,
    sort,
    search,
    fullText,
    k1,
    k2,
    year,
  });
  return queryOptions({
    queryKey: ['getApiV1ClubMembersForNsaClubId', id, params],
    queryFn: () => getResponse('getApiV1ClubMembersForNsaClubId', id, params),
  });
};
export const getApiV1ClubMembersForNsaYears = queryOptions({
  queryKey: ['getApiV1ClubMembersForNsaYears'],
  queryFn: () => getResponse('getApiV1ClubMembersForNsaYears').then(res => res.reverse()),
});
export const getApiV1ClubClubIdMemberEventsDatesUserId = (clubId: number, userId: number, year: string) => {
  return queryOptions({
    queryKey: ['getApiV1ClubClubIdMemberEventsDatesUserId', clubId, userId, year],
    queryFn: () => getResponse('getApiV1ClubClubIdMemberEventsDatesUserId', clubId, userId, { year }),
  });
};
export const getApiV1ClubMemberBeach = (clubId: number, page = 1, limit = DEFAULT_TABLE_PAGE_SIZE, sort?: string, dateTime?: Date) => {
  return queryOptions({
    queryKey: ['getApiV1ClubMemberBeach', clubId, page, limit, sort, dateTime],
    queryFn: () => getResponse('getApiV1ClubMemberBeach', queryParams({
      id: clubId,
      offset: (page - 1) * limit,
      limit,
      sort,
      dateTime: dateToAPIFormatOptional(dateTime),
    })),
  });
};

/**
 * Get team data
 * @param teamId - The team ID
 * @param params - Optional parameters for the API call
 */
export const getApiV1TeamTeamId = (teamId: number, params?: GetApiV1TeamTeamIdParams) => queryOptions({
  queryKey: ['getApiV1TeamTeamId', teamId, params],
  queryFn: () => getResponse('getApiV1TeamTeamId', teamId, params),
});

export const getApiV1ClubMemberBirthNumber = (clubId: number, page: number = 1, limit: number = DEFAULT_TABLE_PAGE_SIZE, sort?: string) => {
  return queryOptions({
    queryKey: ['getApiV1ClubMemberBirthNumber', clubId, limit, page, sort],

    queryFn: () => getResponse('getApiV1ClubMemberBirthNumber', {
      id: clubId,
      limit,
      offset: (page - 1) * limit,
      sort,
    }),
  });
};

export const getApiV1ClubWhisper = (query: string) => {
  return queryOptions({
    queryKey: ['getApiV1ClubWhisper', query],
    queryFn: async () => {
      const response = await getResponse('getApiV1ClubWhisper', { search: query });
      return response.map(item => ({
        id: item.clubId,
        ...item,
      }));
    },
  });
};

/**
 * Get club members
 */
export const getApiV1ClubMember = (params: BaseTableParams<GetApiV1ClubMemberParams>) => queryOptions({
  queryKey: ['getApiV1ClubMember', params],
  queryFn: () => getResponse('getApiV1ClubMember', paginationToApiFormat(params, true)),
});

export const getApiV1ClubMemberWhisper = (clubId: number, fullText: string) => {
  return queryOptions({
    queryKey: ['getApiV1ClubMember', clubId, fullText],
    queryFn: async () => {
      const response = await getResponse('getApiV1ClubMember', {
        id: clubId,
        fullText,
      });

      return response.responseData.map(member => ({
        ...member,
        id: member.userId,
      }));
    },
  });
};

// user-rights
export const getApiV1ClubUserRights = (clubId: number) => queryOptions({
  queryKey: ['getApiV1ClubUserRights', clubId],
  queryFn: () => getResponse('getApiV1ClubUserRights', { clubId }),
});

export const getApiV1ClubMemberBeachDailyOverview = (clubId: number, params?: Omit<GetApiV1ClubMemberBeachDailyOverviewParams, 'clubId'>) => queryOptions({
  queryKey: ['getApiV1ClubMemberBeachDailyOverview', clubId, params],
  queryFn: () => getResponse('getApiV1ClubMemberBeachDailyOverview', queryParams({ ...params, clubId })),
});

export const getApiV1ClubMemberBeachDailyOverviewDetail = (clubId: number, userId: number, year?: string) => queryOptions({
  queryKey: ['getApiV1ClubMemberBeachDailyOverviewDetail', clubId, userId, year],
  queryFn: () => getResponse('getApiV1ClubMemberBeachDailyOverviewDetail', queryParams({
    clubId,
    userId,
    year,
  })),
});
